import { useMemo, useState } from "react";
import { FileOrLink, IAttachmentFormPayload } from "models/attachments";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { useSnackbar } from "@sagecentral/core-ui";

type PostAddSignature = (data: any) => void;
type AddAttachmentSignature = (workItemId: number, payload: IAttachmentFormPayload, draft?: boolean) => Promise<boolean>;

type UseWorkItemAttachmentAddResult = [
    AddAttachmentSignature,
    boolean
];

type UseWorkItemAttachmentSignature = (onAdded?: PostAddSignature, onError?: OnErrorEventHandler) => UseWorkItemAttachmentAddResult;

const makeAttachmentAddUrl = (id: number, draft?: boolean) => draft 
    ? `/api/v3/drafts/workitems/${id}/attachment`
    : `/api/v3/workitems/${id}/attachments`;

const useWorkItemAttachmentAdd: UseWorkItemAttachmentSignature = (onAdded, onError) => {    
    const { displaySnackbar } = useSnackbar();
    const [ working, setWorking ] = useState<boolean>(false); 
    
    const user = useCurrentUser();
    const fetcher = useCentralApi(user, false);

    const addWorkItemAttachment = async (workItemId: number, payload: IAttachmentFormPayload, draft?: boolean) => {        
        if (user && user.id_token && workItemId && payload) {
            setWorking(true);
            try {
                let failed = false;
                let count = payload.files?.length ?? 1;
                for (var i = 0; i < count; i++) {
                    let formData = new FormData();
                    formData.append('typeId', payload.typeId.toString());
                    formData.append('fileOrLink', FileOrLink[payload.fileOrLink]);
                    
                    if (payload.name)
                        formData.append('name', payload.name);
                    if (payload.filePath)
                        formData.append('filePath', payload.filePath);
                    if (payload.url)
                        formData.append('url', payload.url);
                    if (payload.files)
                        formData.append('file', payload.files[i] as File);

                    const urlRoot = makeAttachmentAddUrl(workItemId, draft);

                    const resp = await fetcher(urlRoot, {
                        method: 'POST',
                        body: formData
                    });

                    if (!resp.ok) {
                        failed = true;
                    }
                    
                    const data = await resp.json() as any;
                    onAdded?.(data);
                }

                if (failed) {
                    // RESPOND TO ERRORS                    
                    displaySnackbar?.("error", "We couldn't upload the attachment. Please try again");
                } else {
                    displaySnackbar?.("success", "Success! Attachment uploaded");
                }
                
                return !failed;
            } catch (err) {
                displaySnackbar?.("error", "We couldn't upload the attachment. Please try again")
            } finally {                    
                setWorking(false);
            }
        }

        return false;
    };

    return [
        addWorkItemAttachment,
        working
    ];
};

export default useWorkItemAttachmentAdd;